/**
 * Shared types + constants for the Quote Details gear editor
 * (GearListSection and its extracted row/money helpers). Split out of
 * GearListSection.tsx so the pure row and money modules — and their tests —
 * can depend on the shapes without importing the component (#81).
 */

export interface GearItem {
  id?: string;
  clientKey?: string;
  item_name: string;
  quantity: number;
  days: number;
  notes: string;
  /**
   * Quote heading this line renders under (#21). The server ships it
   * RESOLVED (stored value, else gear-role/type default), the editor edits
   * it, and the save persists it — so the heading is sticky once touched.
   */
  quote_group: string;
  /** Flat $ discount off this line's total (#21). */
  discount: number;
  /**
   * Per-section summarise toggle: when every row in a group carries it, the
   * client-facing quote collapses that group to heading + total. The group
   * header checkbox sets it uniformly across the group's rows.
   */
  quote_group_summarised: boolean;
  /**
   * Quote package this row belongs to (#20); null when the booking has no
   * packages. Round-trips through saves.
   */
  package_key: string | null;
  /** Stable planned-labour identity, carried through quote-detail saves. */
  labour_category?: "setup" | "operation" | "pack_down" | "travel" | null;
  equipment_id?: string | null;
  hire_rate?: number | null;
  type?: string;
  sort_order: number;
  starts_on?: string | null;
  ends_on?: string | null;
  substituted_from_equipment_id?: string | null;
  substituted_from_name?: string | null;
  substitution_reason?: string | null;
  manual_override?: boolean;
  // Hired-in metadata, stamped from the equipment search result on select so
  // the workspace HiredInBanner can surface a freshly-added hired-in line live
  // (before the gear list is saved). Server refetch backfills these too.
  is_hired_in?: boolean;
  suppliers?: { name: string; email?: string | null; phone?: string | null }[];
  /**
   * Transport leg marker (#23): "delivery"/"retrieval" on compute-managed
   * logistics lines. Server-owned and never round-tripped by this editor —
   * since #243 sweep 2 D4 the gear PUT merges logistics rows in place rather
   * than delete+reinserting them, so the column is simply left alone.
   */
  transport_leg?: string | null;
  /**
   * Supplying Warehouse (#304). Server-owned: the picker stamps it when it
   * splits a Role's quantity across sites, and the gear PUT leaves it alone.
   * Null on manual rows and on everything written before migration 128.
   */
  warehouse_id?: string | null;
  warehouse_name?: string | null;
}

/**
 * Loose input shape for rows arriving via props — pre-#21 callers/fixtures
 * may omit the grouped fields; the mount normalisation fills them in.
 */
export type GearItemInput = Omit<
  GearItem,
  "quote_group" | "discount" | "quote_group_summarised" | "package_key"
> & {
  quote_group?: string;
  discount?: number;
  quote_group_summarised?: boolean;
  package_key?: string | null;
};

/** One quote package (#20) as the editor holds it. */
export interface QuotePackage {
  key: string;
  name: string;
}

/** Server-enforced ceiling on packages per booking (#20). */
export const MAX_QUOTE_PACKAGES = 3;

export interface GearShortfall {
  role: string;
  qty_short: number;
  /** Operator-facing Role name, resolved server-side from the Booking's
   *  Profile. Absent when the Role is no longer in the catalogue. */
  role_label?: string;
}

/**
 * One snapshot of everything "Quote Details" persists: the gear rows
 * (PUT gear-items) + the quote settings (PATCH workspace/quote_settings).
 * Shared by the debounced auto-save and the Generate Quote Summary button
 * so both paths write identical payloads (#320).
 */
export interface QuoteDetailsSnapshot {
  gearItems: GearItem[];
  packages: QuotePackage[];
  depositPercent: number;
  quoteValidDays: number;
  quoteNotes: string;
  gearDiscountPercent: number;
  /** Booking-level quote template (#188 follow-on); null inherits the
   * Company default. Deliberately NOT a content-staleness input — template
   * drift has its own snapshot compare (quote_template_stale). */
  quoteTemplateId: string | null;
}

/** Fallback heading for rows the server couldn't classify (new manual rows). */
export const FALLBACK_QUOTE_GROUP = "Other Equipment";

/** Server default heading for logistics lines (quoteGroupTransport). */
export const TRANSPORT_QUOTE_GROUP = "Transport";

/** Server default heading for service lines (quoteGroupLabour). */
export const LABOUR_QUOTE_GROUP = "Labour";

export type GearRowClassification = "preferred" | "split" | "substituted";
